import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { MySpend } from './pages/MySpend';
import { Login } from './pages/Login';
import { Settings } from './pages/Settings';
import { AdminUsers } from './pages/AdminUsers';
import { Sync } from './pages/admin/Sync';
import { Groups } from './pages/admin/Groups';
import { GroupDetail } from './pages/admin/GroupDetail';
import { Notifications } from './pages/admin/Notifications';
import { ApiKeys } from './pages/admin/ApiKeys';
import { useCurrentUser } from './hooks/useCurrentUser';
import { useIsAdmin } from './hooks/useIsAdmin';
import { apiClient } from './lib/api';
import { getClientConfig } from './lib/config';

export default function App() {
  const { data: user, isLoading } = useCurrentUser();
  const isAdmin = useIsAdmin();
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const navClass = ({ isActive }: { isActive: boolean }) =>
    `px-3 py-2 rounded-md text-sm font-medium transition-colors ${
      isActive ? 'bg-atg-navy text-white' : 'text-slate-700 hover:bg-slate-200'
    }`;

  async function signOut() {
    const cfg = await getClientConfig();
    if (cfg.authProvider === 'Okta' || cfg.authProvider === 'Entra') {
      // Server-side OIDC sign-out: clears the local cookie, ends the IdP session, and
      // redirects back to the app root. Top-level navigation, not an XHR.
      window.location.assign('/api/auth/signout');
      return;
    }
    try {
      await apiClient.post('/api/auth/logout', {});
    } catch {
      // ignore — clear local state regardless
    }
    sessionStorage.removeItem('access_token');
    await qc.invalidateQueries({ queryKey: ['me'] });
    navigate('/login');
  }

  // The login page is always reachable, even while unauthenticated. Render it standalone
  // (it is a full-screen page, not part of the app shell).
  if (location.pathname === '/login') {
    return <Login />;
  }

  // Auth gate: until we know who the user is, hold the render. Once we know they are not
  // authenticated, send them to the dedicated /login page rather than the app shell.
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">
        Loading...
      </div>
    );
  }

  if (!user?.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-6">
          <div className="text-lg font-bold text-atg-navy">DevSpend</div>
          <nav className="flex gap-1 flex-wrap">
            <NavLink to="/" end className={navClass}>My Spend</NavLink>
            {isAdmin && (
              <>
                <span className="self-center mx-1 text-slate-300">|</span>
                <NavLink to="/admin/groups" className={navClass}>Groups</NavLink>
                <NavLink to="/admin/notifications" className={navClass}>Notifications</NavLink>
                <NavLink to="/admin/apikeys" className={navClass}>API Keys</NavLink>
                <NavLink to="/admin/sync" className={navClass}>Sync</NavLink>
                <NavLink to="/admin/users" className={navClass}>Users</NavLink>
              </>
            )}
          </nav>
          <div className="ml-auto flex items-center gap-3 text-sm text-slate-600">
            <NavLink to="/settings" className="text-slate-600 hover:underline">Settings</NavLink>
            <span className="hidden sm:inline">{user.email}</span>
            <button onClick={signOut} className="text-atg-blue hover:underline">Sign out</button>
          </div>
        </div>
      </header>
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 py-6">
        <Routes>
          <Route path="/" element={<MySpend />} />
          <Route path="/login" element={<Login />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/admin/groups" element={<Groups />} />
          <Route path="/admin/groups/:id" element={<GroupDetail />} />
          <Route path="/admin/notifications" element={<Notifications />} />
          <Route path="/admin/apikeys" element={<ApiKeys />} />
          <Route path="/admin/sync" element={<Sync />} />
          <Route path="/admin/users" element={<AdminUsers />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <footer className="border-t border-slate-200 py-3 text-center text-xs text-slate-500">
        DevSpend &middot; Alliance Technical Group
      </footer>
    </div>
  );
}
